


























































































import { Component, Vue } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import { Branch } from '@/api/model/branch'
import { EmmaDefaults, EmmaDefaultsEntry, ReferralStatus, ReferralType } from '@/api/types'
import { InputSelectOption } from '@/helpers/options'
import { PipelineAPI } from '@/api/backend-client'
import { Pipeline } from '@/api/model/pipeline'
import TooltipDecorator from '@/components/input/label/decorator/TooltipDecorator.vue'

const smartMarketing = namespace('smartMarketing')
const pipelineConnections = namespace('pipelineConnections')

@Component({
    components: {
        TooltipDecorator,
    },
})
export default class CompanySettingsSmartMarketingDefaultTeams extends Vue {
    @smartMarketing.State readonly defaultTeams!: string[]
    @smartMarketing.State readonly defaultStatus!: string
    @smartMarketing.State readonly emmaDefaults!: EmmaDefaults
    @smartMarketing.Action readonly updateDefaultTeams!: (defaultTeams: string[]) => Promise<void>
    @smartMarketing.Action readonly updateDefaultStatus!: (defaultStatus: string) => Promise<void>
    @smartMarketing.Action readonly updateEmmaDefaults!: (emmaDefaults: EmmaDefaults) => Promise<void>
    @smartMarketing.Getter readonly canUseActiveCampaign!: boolean
    @smartMarketing.Getter readonly canUseEmma!: boolean
    @pipelineConnections.Action readonly loadPipelineConnections!: () => Promise<void>
    ownPipelines: Pipeline[] = []
    loading: boolean = true

    mounted() {
        this.loadOwnPipelines()
    }

    async loadOwnPipelines(): Promise<void> {
        this.loading = true
        this.ownPipelines = await PipelineAPI.getOwnPipelines()
        this.loading = false
    }

    getEmmaDefaultsEntry(identifier: string): EmmaDefaultsEntry {
        if (!this.emmaDefaults[identifier]) {
            this.updateEmmaDefaults({
                ...this.emmaDefaults,
                [identifier]: {
                    pipeline: null,
                    phase: null,
                    defaultStatus: ReferralStatus.PROCESSED,
                },
            })
        }

        return this.emmaDefaults[identifier]
    }

    setEmmaDefaultsEntry(identifier: string, entry: EmmaDefaultsEntry): void {
        this.updateEmmaDefaults({
            ...this.emmaDefaults,
            [identifier]: entry,
        })
    }

    getEntryValue(identifier: string, key: string): string|null {
        return this.getEmmaDefaultsEntry(identifier)[key]
    }

    setEntryValue(identifier: string, key: string, value: string|null): void {
        this.setEmmaDefaultsEntry(identifier, {
            ...this.getEmmaDefaultsEntry(identifier),
            [key]: value,
        })

        // Auto-select first phase if no phase is selected and status was changed to "open"
        if (key === 'defaultStatus' && value === 'open' && this.getEntryValue(identifier, 'phase') === null) {
            const pipeline = this.ownPipelines.find(v => v.id === this.getEntryValue(identifier, 'pipeline'))

            if (pipeline) {
                this.setEntryValue(identifier, 'phase', pipeline.firstPhase.id)
            }
        }

        // Clear phase if disabled but phase still has a value
        if (this.isEntryDisabled(identifier) && this.getEntryValue(identifier, 'phase') !== null) {
            this.setEntryValue(identifier, 'phase', null)
        }
    }

    isEntryDisabled(identifier: string): boolean {
        if (this.getEntryValue(identifier, 'defaultStatus') === 'processed') {
            return true
        }

        if (this.getEntryValue(identifier, 'pipeline') === null) {
            return true
        }

        return false
    }

    updateDefaultTeam(identifier: string, branchId: string) {
        const defaultTeams = { ...this.defaultTeams }
        defaultTeams[identifier] = branchId

        this.updateDefaultTeams(defaultTeams)
    }

    get internalReferralTypes() {
        return this.$currentCompany.handledInternalReferralTypes
            .map((type: string) => ({
                label: this.$t(`referral.type.${type}`) as string,
                value: type,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    get availableBranches() {
        return this.$currentCompany.branches
            .map((element: Branch) => ({
                label: element.name,
                value: element.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    get availableStatuses() {
        return [
            {
                label: this.$t('referral.status.processed').toString(),
                value: ReferralStatus.PROCESSED,
            },
            {
                // dit is expres dit label
                label: this.$t('forms.edit_smart_marketing_form.emmaDefaults.header.phase').toString(),
                value: ReferralStatus.OPEN,
            },
        ]
    }

    get availableEmmaTypes(): ReferralType[] {
        return [
            ReferralType.PURCHASE,
            ReferralType.SALE,
            ReferralType.APPRAISAL,
            ReferralType.INFORMATION,
        ]
    }

    get emmaTypeOptions(): InputSelectOption[] {
        return this.availableEmmaTypes.map(type => ({
            label: this.$t(`referral.type.${type}`) as string,
            value: type,
        }))
    }

    get pipelineOptions(): InputSelectOption[] {
        return this.ownPipelines
            .map(pipeline => ({
                label: pipeline.name,
                value: pipeline.id,
            }))
    }

    get formIdentifiers(): string[] {
        return [
            'agent_costs_purchase',
            'agent_costs_sale',
            'agent_costs_appraisal',
            'appraisal_appraisal',
            'checklist_purchase',
            'information_information',
        ]
    }

    get formOptions(): Array<{ line1: string, line2: string, value: string }> {
        return this.formIdentifiers
            .map(identifier => ({
                line1: this.$t(`forms.edit_smart_marketing_form.emmaDefaults.identifier.${identifier}.line1`).toString(),
                line2: this.$t(`forms.edit_smart_marketing_form.emmaDefaults.identifier.${identifier}.line2`).toString(),
                value: identifier,
            }))
    }

    buildPhaseOptions(pipelineId: string): InputSelectOption[] {
        if (!pipelineId) {
            return []
        }

        const pipeline = this.ownPipelines.find(v => v.id === pipelineId)
        return pipeline.phases
            .map(phase => ({
                label: phase.name,
                value: phase.id,
            }))
    }
}
